import React, { useContext, useState } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import useMedia from 'use-media';
import { Link } from 'gatsby';

import LanguageContext from '../../contexts/LanguageContext';
import { LinkButton } from '../Button';
import {
  DevicePattern,
  MobileIpad,
  DesktopIpad,
  MobileIphone,
  DesktopIphone,
  DesktopLaptop,
  MobileLaptop,
  ProductsWrapper,
} from './styles';

export const PRICES = {
  'bluli-iphone': {
    EUR: {
      price: 34.99,
      compareAtPrice: 44.99,
    },
    GBP: {
      price: 34.99,
      compareAtPrice: 44.99,
    },
    USD: {
      price: 39.99,
      compareAtPrice: 49.99,
    },
    DKK: {
      price: 269,
      compareAtPrice: 349,
    },
    NOK: {
      price: 379,
      compareAtPrice: 499,
    },
  },
  'bluli-ipad': {
    EUR: {
      price: 39.99,
      compareAtPrice: 49.99,
    },
    GBP: {
      price: 39.99,
      compareAtPrice: 49.99,
    },
    USD: {
      price: 49.99,
      compareAtPrice: 69.99,
    },
    DKK: {
      price: 299,
      compareAtPrice: 379,
    },
    NOK: {
      price: 439,
      compareAtPrice: 539,
    },
  },
  'bluli-laptop': {
    EUR: {
      price: 44.99,
      compareAtPrice: 59.99,
    },
    GBP: {
      price: 44.99,
      compareAtPrice: 59.99,
    },
    USD: {
      price: 49.99,
      compareAtPrice: 49.99,
    },
    DKK: {
      price: 499,
      compareAtPrice: 529,
    },
    NOK: {
      price: 499,
      compareAtPrice: 649,
    },
  },
};

const Product = ({ className = '', product, liveProduct }) => {
  const { currency } = useContext(LanguageContext);
  const [currentVariant, setCurrentVariant] = useState('');
  const isTablet = useMedia({ minWidth: '640px' });
  const { formatMessage, locale } = useIntl();

  const getProductImage = (productType) => {
    if (productType === 'iPad') return isTablet ? <DesktopIpad /> : <MobileIpad className="mb-4" />;
    if (productType === 'Laptop') return isTablet ? <DesktopLaptop /> : <MobileLaptop className="mb-4" />;
    return isTablet ? <DesktopIphone /> : <MobileIphone className="mb-4" />;
  };

  if (!product) return null;

  const { productType, handle, variants } = product;
  const variant = variants.find(({ shopifyId }) => shopifyId === currentVariant);
  const { shopifyId, title } = variant || variants[0];
  const price = PRICES[handle][currency].price;
  const compareAtPrice = PRICES[handle][currency].compareAtPrice;

  let livePrice, liveCompareAtPrice, liveVariant;
  if (liveProduct) {
    liveVariant = liveProduct.node.variants.edges.find(({ node }) => node.title === title);
    if (liveVariant) {
      livePrice = liveVariant.node.price;
      liveCompareAtPrice = liveVariant.node.compareAtPrice;
    }
  }

  return (
    <div className={`relative flex flex-col pt-12 sm:pt-0 sm:mb-8 ${className}`}>
      <div className="absolute top-0 left-0 h-48 sm:h-full w-full sm:w-48 md:w-64 flex items-end sm:items-center justify-start sm:justify-center pointer-events-none">
        {getProductImage(productType)}
      </div>
      <div className="px-4 py-6 pt-20 sm:pl-16 md:pl-32 sm:py-10 sm:pr-8 mt-16 sm:mt-0 sm:ml-24 md:ml-32 bg-orange-100 border-b-4 border-t-4 sm:border-8 border-blue-900 flex flex-col items-start">
        <Link
          to={`/product/${handle}${currentVariant ? `?variant=${currentVariant}` : ''}`}
          className="font-heading font-bold text-xl mb-2 hover:underline">
          {formatMessage({ id: `products.${handle}.title` })}
        </Link>
        <div className="font-heading mb-4">
          <span className="inline-block pr-3 font-bold text-xl sm:text-2xl text-blue-600">
            {Intl.NumberFormat(locale, { style: 'currency', currency }).format(livePrice || price)}
          </span>
          <span className="line-through sm:text-xl">
            {Intl.NumberFormat(locale, { style: 'currency', currency }).format(liveCompareAtPrice || compareAtPrice)}
          </span>
        </div>
        <div className="flex flex-wrap mb-4">
          {variants.map(({ id, title, shopifyId }) => (
            <div
              key={id}
              onClick={() => setCurrentVariant(shopifyId)}
              className={`border border-blue-900 mr-2 px-2 rounded-sm mb-2 text-xs hover:bg-white cursor-pointer ${
                shopifyId === currentVariant && 'bg-white'
              }`}>
              {title}
            </div>
          ))}
        </div>
        <LinkButton href={`/${locale}/product/${handle}${currentVariant ? `?variant=${shopifyId}` : ''}`}>
          {formatMessage({ id: 'products.cta' })}
        </LinkButton>
      </div>
    </div>
  );
};

export const DeviceProducts = ({ products, liveProducts }) => {
  const { formatMessage } = useIntl();

  const iPhone = products.find(({ node: { handle } }) => handle === 'bluli-iphone');
  const iPad = products.find(({ node: { handle } }) => handle === 'bluli-ipad');
  const laptop = products.find(({ node: { handle } }) => handle === 'bluli-laptop');
  const iPhoneLive = liveProducts && liveProducts.find(({ node: { handle } }) => handle === 'bluli-iphone');
  const iPadLive = liveProducts && liveProducts.find(({ node: { handle } }) => handle === 'bluli-ipad');
  const laptopLive = liveProducts && liveProducts.find(({ node: { handle } }) => handle === 'bluli-laptop');

  return (
    <section className="py-12 md:py-24">
      <h2 className="xl:container mx-auto px-4 sm:px-6 lg:px-8 w-full text-2xl md:text-4xl lg:text-6xl font-heading font-bold mb-0 sm:mb-12 lg:mb-16 md:text-center">
        {formatMessage({ id: 'products.title' })}
      </h2>
      <div className="relative flex flex-col">
        <DevicePattern className="hidden sm:block absolute w-full" />
        <ProductsWrapper className="sm:px-16">
          {iPhone && iPhone.node ? <Product product={iPhone.node} liveProduct={iPhoneLive} /> : null}
          {iPad && iPad.node ? <Product product={iPad.node} liveProduct={iPadLive} /> : null}
          {laptop && laptop.node ? <Product product={laptop.node} liveProduct={laptopLive} /> : null}
        </ProductsWrapper>
      </div>
    </section>
  );
};
